import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

interface AgentResultItemProps {
  agent: Agent;
  selected: boolean;
  expanded: boolean;
  isLoadingShifts: boolean;
  copied?: boolean;
  onClick: () => void;
}

export const AgentResultItem = forwardRef<HTMLDivElement, AgentResultItemProps>((props, ref) => {
  const {
    agent,
    selected,
    expanded,
    isLoadingShifts,
    copied: copiedFromKeyboard = false,
    onClick,
  } = props;
  const [copiedFromClick, setCopiedFromClick] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  const isCopied = copiedFromKeyboard || copiedFromClick;

  const handleCopy = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      navigator.clipboard.writeText(agent.userId).then(
        () => {
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
          }
          setCopiedFromClick(true);
          timerRef.current = setTimeout(() => {
            setCopiedFromClick(false);
          }, 2000);
        },
        () => {
          showErrorToast("Failed to copy to clipboard");
        }
      );
    },
    [agent.userId]
  );

  return (
    <ListItemButton
      ref={ref}
      selected={selected}
      sx={{
        py: 1,
        px: 2,
        "&.Mui-selected": {
          backgroundColor: "action.selected",
        },
      }}
      onClick={onClick}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        <PersonIcon color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Text sx={{ fontWeight: 500 }}>{agent.name}</Text>
            {selected &&
              (expanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              ))}
            {isLoadingShifts && expanded && <CircularProgress size={14} />}
          </Box>
        }
        secondary={agent.email}
        secondaryTypographyProps={{ variant: "caption" }}
      />
      <Tooltip title={isCopied ? "Copied!" : "Copy ID"}>
        <IconButton size="small" color={isCopied ? "success" : "default"} onClick={handleCopy}>
          {isCopied ? (
            <CheckIcon sx={{ fontSize: 16 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
});

AgentResultItem.displayName = "AgentResultItem";
